// @ts-nocheck
/**
 * AdminDashboard - 管理后台主面板
 *
 * 功能模块：
 * - PromptLab: 提示词管理
 * - ApiControl: API配置管理
 * - VoiceStudio: 音色设置
 * - SystemMonitor: 系统监控
 * - UserManagement: 用户管理
 */

import React, { useState, useEffect } from 'react';
import { apiGet } from '../../utils/apiClient';
import PromptLab from './PromptLab';
import ApiControl from './ApiControl';
import VoiceStudio from './VoiceStudio';
import SystemMonitor from './SystemMonitor';
import UserManagement from './UserManagement';
import './AdminDashboard.css';

interface AdminDashboardProps {
  onClose: () => void;
}

interface TabDef {
  id: string;
  label: string;
  icon: string;
  component: React.ComponentType<{ onRefresh?: () => void; onClose?: () => void }>;
}

const TABS: TabDef[] = [
  { id: 'prompts', label: '提示词', icon: '📝', component: PromptLab },
  { id: 'apis', label: 'API配置', icon: '🔌', component: ApiControl },
  { id: 'voices', label: '音色设置', icon: '🎙️', component: VoiceStudio },
  { id: 'system', label: '系统监控', icon: '📊', component: SystemMonitor },
  { id: 'users', label: '用户管理', icon: '👥', component: UserManagement },
];

export default function AdminDashboard({ onClose }: AdminDashboardProps) {
  const [activeTab, setActiveTab] = useState<string>('prompts');
  const [systemStatus, setSystemStatus] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    fetchSystemStatus();
  }, []);

  const fetchSystemStatus = async () => {
    try {
      const response = await apiGet('/api/admin/config/system/status') as any;
      if (response.success) {
        setSystemStatus(response.status);
      }
    } catch (error: any) {
      console.error('获取系统状态失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const ActiveComponent = TABS.find(t => t.id === activeTab)?.component;

  return (
    <div className="admin-dashboard">
      {/* 顶部导航栏 */}
      <header className="admin-dashboard-header">
        <div className="header-left">
          <h1>Mission Control</h1>
          <span className="header-subtitle">PDF MindMap 管理后台</span>
        </div>

        <div className="header-right">
          {/* 系统状态指示器 */}
          <div className="system-indicators">
            <StatusIndicator
              label="数据库"
              status={systemStatus?.database?.status}
              loading={loading}
            />
            <StatusIndicator
              label="Redis"
              status={systemStatus?.redis?.status}
              loading={loading}
            />
          </div>

          <button className="btn-close" onClick={onClose}>
            关闭
          </button>
        </div>
      </header>

      {/* 主内容区 */}
      <div className="admin-dashboard-content">
        {/* 侧边导航 */}
        <nav className="admin-sidebar">
          {TABS.map(tab => (
            <button
              key={tab.id}
              className={`nav-item ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="nav-icon">{tab.icon}</span>
              <span className="nav-label">{tab.label}</span>
            </button>
          ))}

          {/* 统计卡片 */}
          {systemStatus?.config_counts && (
            <div className="sidebar-stats">
              <div className="stat-item">
                <span className="stat-value">{systemStatus.config_counts.prompts}</span>
                <span className="stat-label">提示词</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{systemStatus.config_counts.api_configs}</span>
                <span className="stat-label">API</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{systemStatus.config_counts.voices}</span>
                <span className="stat-label">音色</span>
              </div>
            </div>
          )}
        </nav>

        {/* 内容面板 */}
        <main className="admin-main">
          {ActiveComponent && <ActiveComponent onRefresh={fetchSystemStatus} />}
        </main>
      </div>
    </div>
  );
}

// 状态指示器组件
interface StatusIndicatorProps {
  label: string;
  status: string | undefined;
  loading: boolean;
}

function StatusIndicator({ label, status, loading }: StatusIndicatorProps) {
  const getStatusClass = (): string => {
    if (loading) return 'loading';
    if (status === 'healthy') return 'healthy';
    return 'unhealthy';
  };

  return (
    <div className={`status-indicator ${getStatusClass()}`}>
      <span className="status-dot"></span>
      <span className="status-label">{label}</span>
    </div>
  );
}
